import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HelpCircle, ChevronDown, ArrowRight, Phone, Mail } from "lucide-react";
import { PageHeader, FinalCTA } from "@/components/PageHeader";

const faqs = [
  { q: "What is a Medicaid waiver?", a: "A Medicaid waiver allows Medicaid to 'waive' certain federal requirements so services can be provided in a person's home or community rather than an institutional setting. In Virginia, DBHDS administers DD waivers (Community Living, Family & Individual Supports, and Building Independence) that fund services like Supportive In-Home, Personal Care, and Residential Supported Living." },
  { q: "How do I get waiver services?", a: "Start by contacting your local Community Services Board (CSB) and requesting a screening. The CSB will determine eligibility and place the individual on the DD Waiver waitlist. Once a slot is offered, a Support Coordinator will work with your family to build an Individual Support Plan (ISP) and choose a licensed provider — such as WeCare II — to deliver services." },
  { q: "How long does it take to get a waiver?", a: "Your support coordinator at the local CSB assigns the priority: Priority One means services are needed within one year, Priority Two within one to five years, and Priority Three in five or more years. Waiver slots are then recommended by a local Waiver Slot Assignment Committee. Your CSB is the best source for where things currently stand in your area." },
  { q: "What does a Medicaid waiver pay for?", a: "DD waivers can pay for Supportive In-Home Services, Personal Care Assistance, Residential Supported Living, Respite, Day Support, Employment Supports, Assistive Technology, Environmental Modifications, and more — depending on which waiver an individual is enrolled in and what is authorized in the ISP." },
  { q: "What waivers does WeCare II accept?", a: "WeCare II accepts the Virginia DD waivers: Community Living (CL), Family and Individual Supports (FIS), and Building Independence (BI). We coordinate directly with Support Coordinators and Case Managers to ensure billing and documentation align with DBHDS requirements." },
  { q: "Do you accept private pay?", a: "Yes. In addition to Medicaid waivers, we can arrange private-pay arrangements for families who need supportive services outside of the waiver framework. Contact our office to discuss rates and availability." },
  { q: "What areas do you serve?", a: "WeCare II serves individuals and families throughout Hampton Roads, the Eastern Shore, and surrounding Virginia communities. We operate two offices — 3877 Holland Road #508-B, Virginia Beach, VA 23452, and 36304 Lankford Hwy, Belle Haven, VA 23306." },
];

function Item({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className={`bg-white rounded-2xl border ${open ? "border-brand-orange/40 shadow-[0_16px_36px_rgba(30,42,120,0.06)]" : "border-slate-100"}`} data-testid={`faq-${idx}`}>
      <button onClick={() => setOpen(v => !v)}
        className="w-full flex items-center justify-between text-left px-6 md:px-8 py-5 gap-4"
        data-testid={`faq-toggle-${idx}`}
        style={{ transition: "background-color 200ms ease" }}>
        <div className="flex items-start gap-4">
          <div className={`w-9 h-9 rounded-xl grid place-items-center shrink-0 ${open ? "bg-brand-orange text-white" : "bg-orange-50 text-brand-orange"}`}
            style={{ transition: "background-color 200ms ease, color 200ms ease" }}>
            <HelpCircle className="w-4 h-4"/>
          </div>
          <div className="font-outfit text-base md:text-lg font-semibold text-brand-navy leading-snug">{q}</div>
        </div>
        <ChevronDown className={`w-5 h-5 text-slate-400 shrink-0 ${open ? "rotate-180" : ""}`} style={{ transition: "transform 250ms ease" }}/>
      </button>
      {open && (
        <div className="px-6 md:px-8 pb-6 pl-[4.5rem] md:pl-[5rem] text-sm text-slate-600 leading-relaxed">
          {a}
        </div>
      )}
    </div>
  );
}

export default function Resources() {
  return (
    <div data-testid="resources-page">
      <PageHeader
        eyebrow="Family Resources"
        title="Everything you need to know about"
        accent="Medicaid waiver"
        kicker=" services."
        subtitle="Navigating waivers can feel overwhelming. We've gathered the most common questions we hear from families and support coordinators — with clear, honest answers."
        data-testid="resources-header"
      />

      <section className="wc-container pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-3">
            {faqs.map((f, i) => <Item key={f.q} idx={i} q={f.q} a={f.a}/>)}
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl p-6 bg-brand-navy text-white relative overflow-hidden">
              <svg className="absolute -top-16 -right-16 w-52 h-52 pointer-events-none opacity-50" viewBox="0 0 200 200" aria-hidden="true"><circle cx="100" cy="100" r="100" fill="#f39325" fillOpacity="0.15"/></svg>
              <div className="relative">
                <div className="text-[10px] uppercase tracking-[0.24em] text-orange-300 font-bold">Still have questions?</div>
                <h3 className="mt-2 font-outfit text-xl md:text-2xl font-semibold">Our team is here to help.</h3>
                <p className="mt-3 text-white/80 text-sm leading-relaxed">Reach out and one of our intake specialists will walk you through the process step by step.</p>
                <div className="mt-4 space-y-2">
                  <a href="tel:7579519555" className="flex items-center gap-3 rounded-xl bg-white/10 hover:bg-white/15 px-4 py-3" style={{transition:"background-color 200ms ease"}}>
                    <Phone className="w-4 h-4 text-brand-orange"/><span className="text-sm font-semibold">757-951-9555</span>
                  </a>
                  <a href="mailto:info@wecareii.com" className="flex items-center gap-3 rounded-xl bg-white/10 hover:bg-white/15 px-4 py-3" style={{transition:"background-color 200ms ease"}}>
                    <Mail className="w-4 h-4 text-brand-orange"/><span className="text-sm font-semibold">info@wecareii.com</span>
                  </a>
                </div>
                <Link to="/contact" className="mt-5 wc-btn-orange w-full !uppercase !tracking-widest text-xs font-bold" data-testid="resources-contact-btn">
                  Contact Us <ArrowRight className="w-4 h-4"/>
                </Link>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-slate-100 p-6">
              <div className="text-[10px] uppercase tracking-[0.24em] text-brand-orange font-bold">Support Coordinators</div>
              <h4 className="mt-2 font-outfit text-lg font-semibold text-brand-navy">Referring a client?</h4>
              <p className="mt-2 text-sm text-slate-600">We coordinate directly with case managers and CSB support coordinators statewide.</p>
              <Link to="/refer" className="mt-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest text-brand-orange hover:brightness-90" data-testid="resources-referral-link">
                Refer an individual <ArrowRight className="w-3.5 h-3.5"/>
              </Link>
            </div>
          </aside>
        </div>
      </section>

      <FinalCTA
        title="Ready to Get Started With WeCare II?"
        subtitle="Whether you're looking for services, making a referral, or simply have questions, our team is ready to help."
        primaryLabel="Request Services"
        primaryTo="/contact"
        secondaryLabel="Make a Referral"
        secondaryTo="/refer"
      />
    </div>
  );
}
